import { formatDateToLocal } from '@core/dates/date.utils';
import type { NotificationActionResponse, NotificationResponse } from './notifications.contract';
import {
  isNotificationActionDestiny,
  isNotificationActionName,
  isNotificationActionType,
  isNotificationStatus,
  type AppNotification,
  type NotificationAction,
  type NotificationDynamicFields,
} from './notifications.models';

export function toNotification(response: NotificationResponse): AppNotification {
  return {
    id: response.id ?? '',
    title: response.title ?? '',
    message: response.message ?? '',
    dynamicFields: toDynamicFields(response.dynamic_fields),
    date: response.created ?? '',
    status: isNotificationStatus(response.status) ? response.status : 'read',
    type: response.type ?? '',
    actions: (response.actions ?? []).map(toAction),
  };
}

function toDynamicFields(
  response: NotificationResponse['dynamic_fields'],
): NotificationDynamicFields | null {
  if (!response) {
    return null;
  }

  return {
    invitedBy: response.invited_by ?? '',
    workspaceName: response.workspace_name ?? '',
    actionBy: response.action_by ?? '',
    date: formatDateToLocal(response.date),
    userAffected: response.user_affected ?? '',
  };
}

const INVITATION_URL = /\/workplace\/(accept|decline)\/?$/;

/** An invitation row's buttons: `workplace_accept`, or a URL ending in `/workplace/accept`. */
function isInvitationAction(response: NotificationActionResponse): boolean {
  return (
    response.destiny?.startsWith('workplace_') === true || INVITATION_URL.test(response.url ?? '')
  );
}

function toDestiny(response: NotificationActionResponse): NotificationAction['destiny'] {
  if (isNotificationActionDestiny(response.destiny)) {
    return response.destiny;
  }

  return isInvitationAction(response) ? 'invitation' : 'no_action_needed';
}

function toAction(response: NotificationActionResponse): NotificationAction {
  const name = isNotificationActionName(response.name) ? response.name : 'ok';

  return {
    id: response.id ?? '',
    name,
    url: response.url ?? '',
    destiny: toDestiny(response),
    type: isNotificationActionType(response.type)
      ? response.type
      : name === 'decline'
        ? 'cancel'
        : 'default',
    isActive: response.is_active === true,
  };
}
